/**
 * Business Capabilities Controller Module
 * Sprint 26: Business Capability Service Model
 *
 * Handles Business Capabilities page initialization and data loading.
 * Displays enterprise capability-to-platform mapping matrix with:
 * - Front Office and Back Office business units
 * - Capability columns (Sales, Processing, Help Desk, Portal, Admin, HR)
 * - Platform icons (Salesforce, ServiceNow, BMC) with click-through to service maps
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only business capabilities page logic
 * - Template Method: Uses BaseController hooks
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Office type enum matching backend
 */
type OfficeType = { FrontOffice: null } | { BackOffice: null };

/**
 * Platform info in matrix entry
 */
interface MatrixPlatform {
    platform_id: string;
    name: string;
    is_primary: boolean;
    icon_url: string | null;
    service_map_id: string | null;
}

/**
 * Platform summary from backend - nested in MatrixEntry.platforms array
 */
interface PlatformSummary {
    platform_id: string;
    name: string;
    is_primary: boolean;
    icon_url: string | null;
    service_map_id?: string | null;
}

/**
 * Matrix entry from backend get_capability_matrix
 * Each entry has a platforms ARRAY containing all platforms for that BU+capability
 */
interface MatrixEntry {
    office_type: OfficeType;
    business_unit_id: string;
    business_unit_name: string;
    capability_id: string;
    capability_name: string;
    platforms: PlatformSummary[];  // Array of all platforms for this cell
}

/**
 * Capability statistics
 */
interface CapabilityStatistics {
    total_business_units: number;
    front_office_units: number;
    back_office_units: number;
    total_capabilities: number;
    total_platforms: number;
    total_assignments: number;
    platform_usage: Array<[string, number]>;
}

/**
 * Platform display config for icons and service map linkage
 * Centralized configuration for all platform-related settings
 */
interface PlatformDisplayConfig {
    abbrev: string;
    bgClass: string;
    textClass: string;
    serviceMapId: string;  // Links to vector_db service map
    legendElementId: string;  // HTML element ID for legend click handler
}

/**
 * Grouped business unit data for rendering
 */
interface BusinessUnitRow {
    business_unit_id: string;
    business_unit_name: string;
    office_type: OfficeType;
    capabilities: Map<string, MatrixPlatform[]>;
}

// ============================================================================
// Controller Implementation
// ============================================================================

/**
 * BusinessCapabilitiesController class
 * Manages the business capabilities page
 */
export class BusinessCapabilitiesController extends BaseController {
    private matrixEntries: MatrixEntry[] = [];
    /**
     * Centralized platform configuration - Single Source of Truth
     * Update service map IDs here when platform mappings change
     */
    private platformDisplayConfigs: Map<string, PlatformDisplayConfig> = new Map([
        ['salesforce', {
            abbrev: 'SF',
            bgClass: 'bg-blue-100',
            textClass: 'text-blue-700',
            serviceMapId: 'Salesforce Platform',
            legendElementId: 'legend-salesforce'
        }],
        ['servicenow', {
            abbrev: 'SN',
            bgClass: 'bg-green-100',
            textClass: 'text-green-700',
            serviceMapId: 'ServiceNow Platform',
            legendElementId: 'legend-servicenow'
        }],
        ['bmc', {
            abbrev: 'BMC',
            bgClass: 'bg-orange-100',
            textClass: 'text-orange-700',
            serviceMapId: 'BMC Software Platform',
            legendElementId: 'legend-bmc'
        }]
    ]);

    // Ordered capability IDs for columns
    private capabilityOrder = ['sales', 'processing', 'helpdesk', 'portal', 'admin', 'hr'];

    constructor(services: ControllerServices) {
        super(services);
    }

    getPageName(): PageName {
        return 'business-capabilities';
    }

    protected async doInit(): Promise<void> {
        await this.loadCapabilityData();
    }

    /**
     * Setup event listeners for page interactions
     */
    protected setupEventListeners(): void {
        // Refresh button
        const refreshBtn = document.getElementById('refresh-capabilities-btn');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this.loadCapabilityData());
        }

        // Close modal buttons
        const closeModalBtn = document.getElementById('close-service-map-modal');
        const closeServiceMapBtn = document.getElementById('close-service-map-btn');

        if (closeModalBtn) {
            closeModalBtn.addEventListener('click', () => this.closeServiceMapModal());
        }
        if (closeServiceMapBtn) {
            closeServiceMapBtn.addEventListener('click', () => this.closeServiceMapModal());
        }

        // Close modal on backdrop click
        const modal = document.getElementById('service-map-modal');
        if (modal) {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    this.closeServiceMapModal();
                }
            });
        }

        // Platform legend click handlers
        this.setupLegendClickHandlers();

        console.log('Business capabilities event listeners setup complete');
    }

    /**
     * Setup click handlers for platform legend items
     * Uses centralized platformDisplayConfigs for service map mappings
     */
    private setupLegendClickHandlers(): void {
        // Iterate over centralized platform config (Single Source of Truth)
        this.platformDisplayConfigs.forEach((config, _platformKey) => {
            const element = document.getElementById(config.legendElementId);
            if (element) {
                element.addEventListener('click', (e) => {
                    // Stop event from bubbling to modal backdrop handler
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    console.log(`Legend clicked: ${config.abbrev} -> ${config.serviceMapId}`);
                    this.openServiceMap(config.serviceMapId, config.abbrev);
                });
            }
        });
    }

    /**
     * Load capability data from vector_db
     */
    public async loadCapabilityData(): Promise<void> {
        console.log('Loading business capability data from vector_db...');

        if (!this.canisterClient) {
            console.error('CanisterClient not available');
            this.showError('capability-stats', 'Canister client not available');
            return;
        }

        try {
            // Load statistics and matrix in parallel
            const [statsResult, matrixResult] = await Promise.all([
                this.canisterClient.callCanister('vector_db', 'get_capability_statistics', []),
                this.canisterClient.callCanister('vector_db', 'get_capability_matrix', [])
            ]);

            console.log('Capability statistics:', statsResult);
            console.log('Capability matrix:', matrixResult);

            // Update statistics
            if (statsResult) {
                this.updateStatistics(statsResult);
            }

            // Process and display matrix
            // The matrix result is the data property or the direct array
            const matrixData = matrixResult?.data || matrixResult;
            if (Array.isArray(matrixData)) {
                this.matrixEntries = matrixData;
                this.renderCapabilityMatrix();
            } else {
                console.error('Matrix result is not an array:', matrixData);
                this.showError('front-office-tbody', 'Invalid matrix data format');
            }
        } catch (error) {
            console.error('Error loading capability data:', error);
            this.showError('capability-stats', 'Failed to load capability data');
        }
    }

    /**
     * Update statistics cards
     */
    private updateStatistics(stats: CapabilityStatistics): void {
        // Handle both direct properties and nested data
        const data = (stats as any)?.data || stats;

        this.updateElementText('stat-total-bu', data.total_business_units || 0);
        this.updateElementText('stat-front-office', data.front_office_units || 0);
        this.updateElementText('stat-back-office', data.back_office_units || 0);
        this.updateElementText('stat-capabilities', data.total_capabilities || 0);
        this.updateElementText('stat-platforms', data.total_platforms || 0);
        this.updateElementText('stat-assignments', data.total_assignments || 0);
    }

    /**
     * Render the capability matrix tables
     */
    private renderCapabilityMatrix(): void {
        // Group entries by business unit
        const businessUnits = this.groupByBusinessUnit();

        // Split into front and back office
        const frontOffice: BusinessUnitRow[] = [];
        const backOffice: BusinessUnitRow[] = [];

        for (const bu of businessUnits.values()) {
            if ('FrontOffice' in bu.office_type) {
                frontOffice.push(bu);
            } else {
                backOffice.push(bu);
            }
        }

        // Sort alphabetically by name
        frontOffice.sort((a, b) => a.business_unit_name.localeCompare(b.business_unit_name));
        backOffice.sort((a, b) => a.business_unit_name.localeCompare(b.business_unit_name));

        // Render tables
        this.renderBusinessUnitTable('front-office-tbody', frontOffice);
        this.renderBusinessUnitTable('back-office-tbody', backOffice);
    }

    /**
     * Group matrix entries by business unit
     * Each entry has a platforms ARRAY containing all platforms for that BU+capability
     */
    private groupByBusinessUnit(): Map<string, BusinessUnitRow> {
        const groups = new Map<string, BusinessUnitRow>();

        for (const entry of this.matrixEntries) {
            // Get or create business unit
            let bu = groups.get(entry.business_unit_id);
            if (!bu) {
                bu = {
                    business_unit_id: entry.business_unit_id,
                    business_unit_name: entry.business_unit_name,
                    office_type: entry.office_type,
                    capabilities: new Map()
                };
                groups.set(entry.business_unit_id, bu);
            }

            // Get or create platforms array for this capability
            let capabilityPlatforms = bu.capabilities.get(entry.capability_id);
            if (!capabilityPlatforms) {
                capabilityPlatforms = [];
                bu.capabilities.set(entry.capability_id, capabilityPlatforms);
            }

            // Add ALL platforms from this entry's platforms array
            for (const platform of entry.platforms || []) {
                capabilityPlatforms.push({
                    platform_id: platform.platform_id,
                    name: platform.name,
                    is_primary: platform.is_primary,
                    icon_url: platform.icon_url,
                    service_map_id: platform.service_map_id || null
                });
            }
        }

        return groups;
    }

    /**
     * Render a business unit table section
     */
    private renderBusinessUnitTable(tbodyId: string, businessUnits: BusinessUnitRow[]): void {
        const tbody = document.getElementById(tbodyId);
        if (!tbody) {
            console.error(`Table body ${tbodyId} not found`);
            return;
        }

        tbody.innerHTML = '';

        if (businessUnits.length === 0) {
            const row = document.createElement('tr');
            row.innerHTML = `<td colspan="${this.capabilityOrder.length + 1}" class="px-4 py-8 text-center text-slate-400">No business units found</td>`;
            tbody.appendChild(row);
            return;
        }

        for (const bu of businessUnits) {
            const row = document.createElement('tr');
            row.className = 'hover:bg-slate-50';

            // Business unit name cell
            let cells = `<td class="px-4 py-3 font-medium text-slate-800">${this.escapeHtml(bu.business_unit_name)}</td>`;

            // Capability cells in order
            for (const capId of this.capabilityOrder) {
                const platforms = bu.capabilities.get(capId) || [];
                cells += this.renderCapabilityCell(platforms);
            }

            row.innerHTML = cells;
            tbody.appendChild(row);
        }
    }

    /**
     * Render a capability cell with platform icons
     */
    private renderCapabilityCell(platforms: MatrixPlatform[]): string {
        if (platforms.length === 0) {
            return `<td class="px-4 py-3 text-center">
                <span class="text-slate-300">-</span>
            </td>`;
        }

        // Sort to put primary first
        const sorted = [...platforms].sort((a, b) => {
            if (a.is_primary && !b.is_primary) return -1;
            if (!a.is_primary && b.is_primary) return 1;
            return 0;
        });

        const icons = sorted.map(platform => {
            const config = this.platformDisplayConfigs.get(platform.platform_id) ||
                { abbrev: platform.platform_id.substring(0, 2).toUpperCase(), bgClass: 'bg-slate-100', textClass: 'text-slate-700' };

            const primaryRing = platform.is_primary ? 'ring-2 ring-yellow-400 ring-offset-1' : '';
            const serviceMapId = platform.service_map_id || '';
            const clickHandler = serviceMapId
                ? `onclick="window.businessCapabilitiesController?.openServiceMap('${serviceMapId}', '${platform.name}')"`
                : '';
            const cursorClass = serviceMapId ? 'cursor-pointer hover:scale-110' : '';
            const title = platform.is_primary ? `${platform.name} (Primary)` : platform.name;

            return `<div class="w-8 h-8 rounded ${config.bgClass} flex items-center justify-center ${primaryRing} ${cursorClass} transition-transform"
                        title="${title}"
                        ${clickHandler}>
                <span class="${config.textClass} font-bold text-xs">${config.abbrev}</span>
            </div>`;
        }).join('');

        return `<td class="px-4 py-3 text-center">
            <div class="flex items-center justify-center gap-1">
                ${icons}
            </div>
        </td>`;
    }

    /**
     * Open service map modal for a platform
     * Uses ServiceMapController.loadServiceMap() - same as View button in Service Models
     */
    public async openServiceMap(serviceMapId: string, platformName: string): Promise<void> {
        console.log(`Opening service map: ${serviceMapId} for ${platformName}`);

        // Use the same approach as the View button in Service Models
        // This uses ServiceMapController.loadServiceMap which handles modal display and rendering
        if ((window as any).serviceMapController && typeof (window as any).serviceMapController.loadServiceMap === 'function') {
            (window as any).serviceMapController.loadServiceMap(serviceMapId);
        } else {
            console.warn('ServiceMapController not available, attempting fallback');
            // Fallback: try to initialize and load
            const modal = document.getElementById('service-map-modal');
            if (modal) {
                modal.classList.remove('hidden');
            }
        }
    }

    // OOA/OOD: Service map rendering is delegated to ServiceMapController.loadServiceMap()
    // This is the same method used by the View button in Service Models page (Single Responsibility Principle)

    /**
     * Close the service map modal
     * Uses the shared service-map-modal from components/modals/service-map.html
     */
    private closeServiceMapModal(): void {
        // Use the global hideServiceMap function if available
        if (typeof (window as any).hideServiceMap === 'function') {
            (window as any).hideServiceMap();
        } else {
            // Fallback: directly hide the modal
            const modal = document.getElementById('service-map-modal');
            if (modal) {
                modal.classList.add('hidden');
            }
        }
    }

    /**
     * Get the matrix entries (for external access)
     */
    public getMatrixEntries(): MatrixEntry[] {
        return this.matrixEntries;
    }
}

// Export for global access (used by onclick handlers)
declare global {
    interface Window {
        businessCapabilitiesController: BusinessCapabilitiesController | null;
    }
}
